import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router'
import { connect } from 'react-redux'
import { get } from 'lodash'
import { logError, modal, notification, setGlobalLoading } from 'redux/modules/app'
import {
  awards as awardsApi,
  participants as participantsApi,
  residentialProjects as residentialProjectsApi,
  tasks as tasksApi
} from 'redux/modules/api'
import { updateScheme } from 'models/residential-projects'
import { changeAssessorSchema } from 'models/assessors'
import { parseAuthCode } from 'utils/authCode'

function decorator(config = {}) {
  return ComposedComponent => {
    @withRouter
    @connect(
      ({ api, auth }) => ({
        residentialProjects: api.residentialProjects,
        localRpApprovalTasks: api.tasks.rp.approval,
        localRpSignoffTasks: api.tasks.rp.signoff,
        user: auth.user,
        activeOrganizationId: auth.organization.id
      }),
      {
        ...residentialProjectsApi,
        notification,
        modal,
        setGlobalLoading,
        logError,
        getParticipant: participantsApi.getParticipant,
        getAwards: awardsApi.getAwards,
        getTasksApprovalRp: tasksApi.rp.getTasksApprovalRp,
        getTasksSignoffRp: tasksApi.rp.getTasksSignoffRp,
        getTasksOverview: tasksApi.overview.getTasksOverview
      }
    )
    class ResidentialProjects extends Component {
      static propTypes = {
        params: PropTypes.object,
        decoRpId: PropTypes.number,
        user: PropTypes.object,
        activeOrganizationId: PropTypes.number.isRequired,
        route: PropTypes.object,
        localRpApprovalTasks: PropTypes.object,
        localRpSignoffTasks: PropTypes.object,
        getPublicRpEvent: PropTypes.func.isRequired,
        getRpEvent: PropTypes.func.isRequired,
        updateResidentialProject: PropTypes.func.isRequired,
        setupResidentialProject: PropTypes.func.isRequired,
        changeRpAssessor: PropTypes.func.isRequired,
        approveResidentialProject: PropTypes.func.isRequired,
        signoffResidentialProject: PropTypes.func.isRequired,
        getPublicResidentialProjectAssessmentRequests: PropTypes.func
          .isRequired,
        resendRpAssessmentRequest: PropTypes.func.isRequired,
        getParticipant: PropTypes.func.isRequired,
        getTasksApprovalRp: PropTypes.func.isRequired,
        getTasksSignoffRp: PropTypes.func.isRequired,
        getAwards: PropTypes.func.isRequired,
        getResidentialProjectAssessment: PropTypes.func.isRequired,
        signoffRequestResidentialProject: PropTypes.func.isRequired,
        notification: PropTypes.func.isRequired,
        logError: PropTypes.func.isRequired,
        setGlobalLoading: PropTypes.func.isRequired,
        getTasksOverview: PropTypes.func.isRequired,
        changeRpState: PropTypes.func.isRequired,
        modal: PropTypes.func.isRequired,
        getRPReport: PropTypes.func.isRequired
      }

      static defaultProps = {
        params: {}
      }

      componentDidMount() {
        const { params, decoRpId } = this.props

        const rpId = decoRpId || params.id || params.rpId

        config.detail && this.handleGetRpEvent(rpId)
        config.publicDetail && this.handleGetPublicRpEvent(rpId)
      }

      handleGetRpEvent = id => this.props.getRpEvent(id).catch(this.handleError)

      handleGetPublicRpEvent = id =>
        this.props
          .getPublicRpEvent(
            id,
            parseAuthCode(this.props)
          )
          .catch(this.handleError)

      handleUpdateResidentialProject = (id, fields) =>
        this.props
          .updateResidentialProject(id, updateScheme(fields))
          .then(() =>
            this.handleSuccess(
              'Residential project has been updated',
              'notifications.rp_has_been_updated'
            )
          )
          .catch(this.handleError)

      handleSetupResidentialProject = (id, fields) =>
        this.props
          .setupResidentialProject(id, updateScheme(fields))
          .then(() =>
            this.handleSuccess(
              'Residential project has been set up',
              'notifications.rp_has_been_setup'
            )
          )
          .catch(this.handleError)

      handleApproveResidentialProject = (rpId, fields, isAssessorManagementEnabled) =>
        this.props
          .approveResidentialProject(rpId, { approved: true })
          .then(() => {
              if (isAssessorManagementEnabled) {
                this.handleSuccess(
                  'Activity and Assessor have been authorised, Assessor has been sent invitation',
                  'notifications.assessor_and_activity_has_been_approved'
                )
              } else {
                this.handleSuccess(
                  'Residential project has been approved',
                  'notifications.rp_has_been_approved'
                )
              }
            }
          )
          .catch(this.handleError)

      handleReturnApproveResidentialProject = (rpId, fields) =>
        this.props
          .approveResidentialProject(rpId, {
            approved: false,
            note: fields.note,
            reason: fields.reason
          })
          .then(() =>
            this.handleSuccess(
              'Residential project has been returned',
              'notifications.rp_has_been_returned'
            )
          )
          .catch(this.handleError)

      handleSignoffResidentialProject = rpId =>
        this.props
          .signoffResidentialProject(rpId, { approved: true })
          .then(() =>
            this.handleSuccess(
              'Residential project has been signed off',
              'notifications.rp_has_been_signed_off'
            )
          )
          .catch(this.handleError)

      handleReturnSignoffResidentialProject = (rpId, fields) =>
        this.props
          .signoffResidentialProject(rpId, {
            approved: false,
            note: fields.note
          })
          .then(() =>
            this.handleSuccess(
              'Residential project has been returned',
              'notifications.rp_has_been_returned'
            )
          )
          .catch(this.handleError)

      handleChangeRpState = (id, state) => {
        this.props.setGlobalLoading(true)

        this.props
          .changeRpState(id, state)
          .then(() =>
            this.handleSuccess(
              'Residential project state has been changed',
              'notifications.rp_state_has_been_changed'
            )
          )
          .catch(this.handleError)
      }

      handleGetResidentialProjectAssessment = (rpId, rpActionId) =>
        this.props
          .getResidentialProjectAssessment(rpId, rpActionId)
          .then(() => {
          })
          .catch(this.handleError)

      handleGetPublicResidentialProjectAssessmentRequests = (
        rpIdId,
        rpIdActionId
      ) => {
        this.props
          .getPublicResidentialProjectAssessmentRequests(
            rpIdId,
            rpIdActionId,
            parseAuthCode(this.props)
          )
          .then(() => {
          })
          .catch(this.handleError)
      }

      handleSignoffRequestResidentialProject = (id, requestId) =>
        this.props.signoffRequestResidentialProject(id, requestId)

      handleResendRpAssessmentRequest = id => {
        this.props
          .resendRpAssessmentRequest(id)
          .then(() =>
            this.handleSuccess(
              'Assessment request has been sent',
              'notifications.assessment_request_has_been_sent'
            )
          )
          .catch(this.handleError)
      }

      handleGetRPReport = rpId => {
        return this.props.getRPReport(rpId)
      }

      handleChangeRpAssessor = fields =>
        this.props
          .changeRpAssessor(fields.id, changeAssessorSchema(fields))
          .then(() => {
            this.handleSuccess(
              'Assessor has been updated',
              'notifications.assessor_has_been_updated'
            )
          })
          .catch(this.handleError)

      handleSuccess = (text = '', intlKey) => {
        const {
          params,
          localRpApprovalTasks,
          localRpSignoffTasks,
          route,
          decoRpId
        } = this.props

        this.props.notification(text, {
          status: 'success',
          intlKey
        })
        this.props.modal(null)
        config.updateParticipantDetails &&
        this.props.getParticipant(params && params.id)
        config.updateApprovalTasks &&
        this.props.getTasksApprovalRp(localRpApprovalTasks)
        config.updateSignoffTasks &&
        this.props.getTasksSignoffRp(localRpSignoffTasks)
        config.updateSignoffTasks && this.props.getTasksOverview(null, {
          activeOrganizationId: this.props.activeOrganizationId,
          awardLeaderId: this.props.user.personId
        })
        config.updateRpDetail && this.handleGetRpEvent(decoRpId || params.id)

        if (config.updateParticipantAward) {
          get(route, 'awardLevelId') &&
          this.props.getAwards(
            undefined,
            `participant.id=${params && params.id},awardLevel.value=${route.awardLevelId}`
          )
        }
        this.props.setGlobalLoading(false)
      }

      handleError = err => {
        // console.debug('error', err.body && err.body.userMessage)
        // this.props.notification(
        //   err.body && (err.body.userMessage || err.body.developerMessage),
        //   'error'
        // )
        this.props.logError(err, {
          authCode: parseAuthCode(this.props)
        })
        this.props.setGlobalLoading(false)
      }

      render () {
        return (
          <ComposedComponent
            {...this.props}
            updateResidentialProject={this.handleUpdateResidentialProject}
            setupResidentialProject={this.handleSetupResidentialProject}
            approveResidentialProject={this.handleApproveResidentialProject}
            returnApproveResidentialProject={
              this.handleReturnApproveResidentialProject
            }
            signoffResidentialProject={this.handleSignoffResidentialProject}
            returnSignoffResidentialProject={
              this.handleReturnSignoffResidentialProject
            }
            changeRpState={this.handleChangeRpState}
            getResidentialProjectAssessment={
              this.handleGetResidentialProjectAssessment
            }
            getPublicResidentialProjectAssessmentRequests={
              this.handleGetPublicResidentialProjectAssessmentRequests
            }
            onSignoffRequestResidentialProject={
              this.handleSignoffRequestResidentialProject
            }
            resendRpAssessmentRequest={this.handleResendRpAssessmentRequest}
            changeActivityAssessor={this.handleChangeRpAssessor}
            localRpApprovalTasks={undefined}
            getRPReport={this.handleGetRPReport}
          />
        )
      }
    }

    return ResidentialProjects
  }
}

export default decorator
